'use client';

/** Tool names emitted when the bridge cannot resolve a concrete tool id. */
const PLACEHOLDER_TOOL_NAMES = new Set([
  'unknown_tool',
  'unknown tool',
  'unknown',
]);

const FILE_PATH_KEYS = ['path', 'target_file', 'file_path', 'filePath'] as const;

const WRITE_CONTENT_KEYS = ['content', 'contents'] as const;

/**
 * Returns true when the tool name is missing or a generic placeholder.
 */
export function isPlaceholderToolName(name: string | undefined | null): boolean {
  if (name == null) {
    return true;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return true;
  }
  return PLACEHOLDER_TOOL_NAMES.has(trimmed.toLowerCase());
}

function readNonEmptyString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
}

function readFilePath(record: Record<string, unknown>): string | undefined {
  for (const key of FILE_PATH_KEYS) {
    const path = readNonEmptyString(record, key);
    if (path) {
      return path;
    }
  }
  return undefined;
}

function hasWriteLikePayload(record: Record<string, unknown>): boolean {
  return WRITE_CONTENT_KEYS.some(key => record[key] !== undefined && record[key] !== null);
}

/**
 * Infers a canonical tool id from args when the runtime sent a placeholder name.
 * Keep in sync with `CursorSdkBridge.inferCanonicalToolNameFromArgs` in the SDK bridge.
 */
export function inferCanonicalToolNameFromArgs(record: Record<string, unknown>): string | undefined {
  if (
    typeof record.toolName === 'string' && record.toolName.trim()
    || typeof record.tool_name === 'string' && record.tool_name.trim()
  ) {
    return undefined;
  }

  const globPattern = readNonEmptyString(record, 'globPattern') ?? readNonEmptyString(record, 'glob_pattern');
  if (globPattern) {
    return 'glob';
  }

  const command = readNonEmptyString(record, 'command');
  if (command) {
    return 'shell';
  }

  const filePath = readFilePath(record);
  if (filePath) {
    return hasWriteLikePayload(record) ? 'write_file' : 'read_file';
  }

  return undefined;
}

/**
 * Reads `toolName` from Genesys gateway tool payloads (object or JSON string).
 */
export function extractToolNameFromPayload(
  payload: unknown,
  options?: { allowInference?: boolean },
): string | undefined {
  let parsed: unknown = payload;
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return undefined;
    }
    try {
      parsed = JSON.parse(payload) as unknown;
    } catch {
      return undefined;
    }
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return undefined;
  }

  const record = parsed as Record<string, unknown>;
  const toolName = record.toolName;
  if (typeof toolName === 'string' && !isPlaceholderToolName(toolName)) {
    return toolName.trim();
  }

  const snakeCaseToolName = record.tool_name;
  if (typeof snakeCaseToolName === 'string' && !isPlaceholderToolName(snakeCaseToolName)) {
    return snakeCaseToolName.trim();
  }

  const name = record.name;
  if (typeof name === 'string' && !isPlaceholderToolName(name)) {
    return name.trim();
  }

  const globPattern = record.globPattern;
  if (typeof globPattern === 'string' && globPattern.trim()) {
    return 'glob';
  }

  const globPatternSnake = record.glob_pattern;
  if (typeof globPatternSnake === 'string' && globPatternSnake.trim()) {
    return 'glob';
  }

  return options?.allowInference === false ? undefined : inferCanonicalToolNameFromArgs(record);
}

/**
 * Resolves the label shown in tool card headers.
 */
export function resolveToolDisplayName(fallbackName: string, payload?: unknown): string {
  const trimmedFallback = fallbackName.trim();
  const fromPayload = extractToolNameFromPayload(payload, {
    allowInference: isPlaceholderToolName(trimmedFallback),
  });
  if (fromPayload) {
    return fromPayload;
  }

  if (trimmedFallback && !isPlaceholderToolName(trimmedFallback)) {
    return trimmedFallback;
  }

  return trimmedFallback || 'Tool';
}

/**
 * Lowercase key used for specialised tool renderers and routing.
 */
export function resolveToolRoutingKey(fallbackName: string, payload?: unknown): string {
  return resolveToolDisplayName(fallbackName, payload).toLowerCase();
}
